import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { IssueFilterKeys, Ticketed } from '../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';
import { FilterPanel } from '../../generic';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import FilterCardNestedCheckboxes from '../../generic/Filter/FilterCardNestedCheckboxes';
import useFetchIssuesAction from '../IssuesInbox/hooks/useFetchIssuesAction';
import type { SelectOption } from '../../../types';
import type { SelectedOptionsProps } from '../../generic';

// Filter labels by key value
const TICKETED_FILTER_LABELS: Record<string, string> = {
  [Ticketed.Jira]: 'Jira Integration',
  [Ticketed.Custom]: 'URL linked',
};

// Built filter options for checkbox filter
const TICKETED_FILTER_OPTIONS: SelectOption[] = Object.entries(TICKETED_FILTER_LABELS).map(([value, label]) => ({
  label,
  value,
}));

const NOT_TICKETED_LABEL: Record<string, string> = {
  [Ticketed.NoTicket]: 'Not ticketed',
};

// We provide the not nested options as their own array.
const NOT_TICKTED_OPTION: SelectOption[] = Object.entries(NOT_TICKETED_LABEL).map(([value, label]) => ({
  label,
  value,
}));

const TicketIssueFilter: VoidFunctionComponent<Omit<SelectedOptionsProps, 'onSelect'>> = ({ selectedOptions }) => {
  // Since we're using a newer component (FilterCardNestedCheckboxes), we don't use the same onSelect
  // as the other issues filters in favor of just directly changing the filter state.
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const setFilterHasChanged = useSetFilterHasChanged();

  const selectedCheckboxes = (selectedOptions ?? []).map((option: string) => ({
    label: TICKETED_FILTER_LABELS[option] ?? NOT_TICKETED_LABEL[option],
    value: option,
  }));

  const onNestedCheckboxChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.Ticketed]: newSelected.map(({ value }) => value),
    });
    setFilterHasChanged(true);
  };

  return (
    <FilterPanel>
      <FilterCardNestedCheckboxes
        label="Issues"
        options={TICKETED_FILTER_OPTIONS}
        values={selectedCheckboxes}
        onChange={onNestedCheckboxChange}
        parentCheckboxLabel="Ticketed"
      />
      <FilterCardCheckboxes
        options={NOT_TICKTED_OPTION}
        values={selectedCheckboxes}
        onChange={onNestedCheckboxChange}
      />
    </FilterPanel>
  );
};

export default TicketIssueFilter;
